import { ApiResponse } from 'apisauce';
import { IPaginationSnapshot } from 'src/models/pagination/Pagination';
import { IResponseSnapshot } from 'src/models/response/Response';

import {
  IDeptRestructureMap,
  IDeptRestructureMapSnapshot,
  IDeptRestructureSnapshot,
} from '../../models';
import { Api } from '../api/api';
import { getGeneralApiProblem } from '../api/api-problem';

import {
  TDeleteDeptRestructureMapResult,
  TDeleteDeptRestructureResult,
  TGetDeptRestructureMapsResult,
  TGetDeptRestructureResult,
  TGetDeptRestructuresResult,
  TPostDeptRestructureMapResult,
  TPostDeptRestructureResult,
  TUpdateDeptRestructureMapResult,
  TUpdateDeptRestructureResult,
} from './DeptRestructureTypes';

/**
 * # DeptRestructure Api 서비스
 *
 * 조직 개편 이벤트 및 부서 매핑 관리 API
 */
export class DeptRestructureApi {
  private api: Api;

  constructor(api: Api) {
    this.api = api;
  }

  async gets(
    paging: IPaginationSnapshot,
    filters?: {
      restructureTypeCd?: number;
      startDate?: string;
      endDate?: string;
    },
  ): Promise<TGetDeptRestructuresResult> {
    try {
      let url = `/v1/dept-restructures?page=${paging.page}&size=${paging.size}`;

      // 필터 파라미터 추가
      if (filters?.restructureTypeCd) {
        url += `&restructureTypeCd=${filters.restructureTypeCd}`;
      }
      if (filters?.startDate) {
        url += `&startDate=${filters.startDate}`;
      }
      if (filters?.endDate) {
        url += `&endDate=${filters.endDate}`;
      }

      const response: ApiResponse<any> = await this.api.apisauce.get(url);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const deptRestructures = response.data.data;
      const pagination: IPaginationSnapshot = response.data.pagination;
      const responseInfo: IResponseSnapshot = response.data;
      return { kind: 'ok', deptRestructures, pagination, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }

  async get(restructureSid: number): Promise<TGetDeptRestructureResult> {
    try {
      const url = `/v1/dept-restructures/${restructureSid}`;
      const response: ApiResponse<any> = await this.api.apisauce.get(url);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const deptRestructure = response.data.data;
      const responseInfo: IResponseSnapshot = response.data;
      return { kind: 'ok', deptRestructure, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }

  async post(data: IDeptRestructureSnapshot): Promise<TPostDeptRestructureResult> {
    try {
      const url = '/v1/dept-restructures';
      const response: ApiResponse<any> = await this.api.apisauce.post(url, data);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const deptRestructure = response.data.data;
      const responseInfo: IResponseSnapshot = response.data;
      return { kind: 'ok', deptRestructure, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }

  async update(data: IDeptRestructureSnapshot): Promise<TUpdateDeptRestructureResult> {
    try {
      const url = `/v1/dept-restructures/${data.restructureSid}`;
      const response: ApiResponse<any> = await this.api.apisauce.put(url, data);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const deptRestructure = response.data.data;
      const responseInfo: IResponseSnapshot = response.data;
      return { kind: 'ok', deptRestructure, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }

  async delete(restructureSid: number): Promise<TDeleteDeptRestructureResult> {
    try {
      const url = `/v1/dept-restructures/${restructureSid}`;
      const response: ApiResponse<any> = await this.api.apisauce.delete(url);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }
      const responseInfo: IResponseSnapshot = response.data;
      return { kind: 'ok', responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }

  /**
   * 바로 실행 (DRAFT → EXECUTED)
   * 승인 프로세스 생략하고 즉시 부서 트리 변경 실행
   */
  async executeDirect(restructureSid: number): Promise<TPostDeptRestructureResult> {
    try {
      const url = `/v1/dept-restructures/${restructureSid}/execute-direct`;
      const response: ApiResponse<any> = await this.api.apisauce.post(url);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const deptRestructure = response.data.data;
      const responseInfo: IResponseSnapshot = response.data;
      return { kind: 'ok', deptRestructure, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }

  /**
   * 승인 요청 (DRAFT → PENDING)
   */
  async submit(restructureSid: number): Promise<TPostDeptRestructureResult> {
    try {
      const url = `/v1/dept-restructures/${restructureSid}/submit`;
      const response: ApiResponse<any> = await this.api.apisauce.post(url);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const deptRestructure = response.data.data;
      const responseInfo: IResponseSnapshot = response.data;
      return { kind: 'ok', deptRestructure, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }

  /**
   * 승인 (PENDING → APPROVED)
   */
  async approve(restructureSid: number, comment?: string): Promise<TPostDeptRestructureResult> {
    try {
      const url = `/v1/dept-restructures/${restructureSid}/approve`;
      const response: ApiResponse<any> = await this.api.apisauce.post(url, { comment });

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const deptRestructure = response.data.data;
      const responseInfo: IResponseSnapshot = response.data;
      return { kind: 'ok', deptRestructure, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }

  /**
   * 반려 (PENDING → REJECTED)
   */
  async reject(restructureSid: number, reason: string): Promise<TPostDeptRestructureResult> {
    try {
      const url = `/v1/dept-restructures/${restructureSid}/reject`;
      const response: ApiResponse<any> = await this.api.apisauce.post(url, { reason });

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const deptRestructure = response.data.data;
      const responseInfo: IResponseSnapshot = response.data;
      return { kind: 'ok', deptRestructure, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }

  /**
   * 실행 (APPROVED → EXECUTED)
   * 승인 완료 후 실제 부서 트리 변경 실행
   */
  async execute(restructureSid: number): Promise<TPostDeptRestructureResult> {
    try {
      const url = `/v1/dept-restructures/${restructureSid}/execute`;
      const response: ApiResponse<any> = await this.api.apisauce.post(url);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const deptRestructure = response.data.data;
      const responseInfo: IResponseSnapshot = response.data;
      return { kind: 'ok', deptRestructure, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }

  /**
   * 취소 (DRAFT/PENDING → CANCELLED)
   */
  async cancel(restructureSid: number): Promise<TPostDeptRestructureResult> {
    try {
      const url = `/v1/dept-restructures/${restructureSid}/cancel`;
      const response: ApiResponse<any> = await this.api.apisauce.post(url);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const deptRestructure = response.data.data;
      const responseInfo: IResponseSnapshot = response.data;
      return { kind: 'ok', deptRestructure, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }

  // --------------------------------------------------------------------------
  // DeptRestructureMap (부서 매핑) API
  // --------------------------------------------------------------------------

  async getMaps(restructureSid: number): Promise<TGetDeptRestructureMapsResult> {
    try {
      const url = `/v1/dept-restructures/${restructureSid}/maps`;
      const response: ApiResponse<any> = await this.api.apisauce.get(url);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const deptRestructureMaps: IDeptRestructureMap[] = response.data.data;
      const responseInfo: IResponseSnapshot = response.data;
      return { kind: 'ok', deptRestructureMaps, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }

  async postMap(
    restructureSid: number,
    data: IDeptRestructureMapSnapshot,
  ): Promise<TPostDeptRestructureMapResult> {
    try {
      const url = `/v1/dept-restructures/${restructureSid}/maps`;
      const response: ApiResponse<any> = await this.api.apisauce.post(url, data);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const deptRestructureMap = response.data.data;
      const responseInfo: IResponseSnapshot = response.data;
      return { kind: 'ok', deptRestructureMap, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }

  async updateMap(
    restructureSid: number,
    data: IDeptRestructureMapSnapshot,
  ): Promise<TUpdateDeptRestructureMapResult> {
    try {
      const url = `/v1/dept-restructures/${restructureSid}/maps/${data.mapSid}`;
      const response: ApiResponse<any> = await this.api.apisauce.put(url, data);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const deptRestructureMap = response.data.data;
      const responseInfo: IResponseSnapshot = response.data;
      return { kind: 'ok', deptRestructureMap, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }

  async deleteMap(
    restructureSid: number,
    mapSid: number,
  ): Promise<TDeleteDeptRestructureMapResult> {
    try {
      const url = `/v1/dept-restructures/${restructureSid}/maps/${mapSid}`;
      const response: ApiResponse<any> = await this.api.apisauce.delete(url);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }
      const responseInfo: IResponseSnapshot = response.data;
      return { kind: 'ok', responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }
}
